
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../course/components/Header';
import Footer from '../course/components/Footer';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: 'course' | 'licensing' | 'technical' | 'billing';
}

export default function SupportPage() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [openFAQ, setOpenFAQ] = useState<string>('');
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    priority: 'normal'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const faqItems: FAQItem[] = [
    // Course Related
    {
      id: 'faq1',
      question: 'How long do I have access to the course?',
      answer: 'You have lifetime access to the course content, including all future updates and new materials we add.',
      category: 'course'
    },
    {
      id: 'faq2',
      question: 'Can I download the course materials?',
      answer: 'Yes! All checklists, templates, forms, and resources are downloadable. Video lessons are streaming only to protect our content.',
      category: 'course'
    },
    {
      id: 'faq3',
      question: 'Is the course mobile-friendly?',
      answer: 'Absolutely! The course works perfectly on phones, tablets, and computers. Learn anywhere, anytime.',
      category: 'course'
    },
    {
      id: 'faq4',
      question: 'What if I get stuck on a lesson?',
      answer: 'Email us at Info@georgiadealerlicense.com with your specific question. We typically respond within 24 hours.',
      category: 'course'
    },

    // Licensing Related
    {
      id: 'faq5',
      question: 'How long does it take to get a Georgia dealer license?',
      answer: 'The typical timeline is 60-90 days from start to finish, assuming you follow our step-by-step process and submit complete applications.',
      category: 'licensing'
    },
    {
      id: 'faq6',
      question: 'What if my application gets denied?',
      answer: 'Denials are rare when following our system properly. If it happens, we\'ll help you understand why and how to reapply successfully.',
      category: 'licensing'
    },
    {
      id: 'faq7',
      question: 'Do I need a physical location before applying?',
      answer: 'Yes, you need a compliant location secured before submitting your application. Module 2 covers all location requirements in detail.',
      category: 'licensing'
    },
    {
      id: 'faq8',
      question: 'How much does the licensing process cost in total?',
      answer: 'Expect $3,000-$5,000 total including application fees, bond, insurance, education, and other requirements. Our course helps you minimize these costs.',
      category: 'licensing'
    },

    // Technical
    {
      id: 'faq9',
      question: 'I forgot my login password. How do I reset it?',
      answer: 'Click the "Forgot Password" link on the login page, or email us at Info@georgiadealerlicense.com and we\'ll help you reset it.',
      category: 'technical'
    },
    {
      id: 'faq10',
      question: 'The videos won\'t play. What should I do?',
      answer: 'Try refreshing the page, clearing your browser cache, or switching browsers. If issues persist, contact us with your device and browser details.',
      category: 'technical'
    },

    // Billing
    {
      id: 'faq11',
      question: 'Do you offer refunds?',
      answer: 'We do not offer refunds on our course purchases. All sales are final. We encourage you to review the course content and materials thoroughly before making your purchase decision.',
      category: 'billing'
    },

    {
      id: 'faq12',
      question: 'Can I upgrade from the payment plan to full payment?',
      answer: 'Absolutely! Contact us and we\'ll calculate the difference and upgrade your account immediately.',
      category: 'billing'
    }
  ];

  const categories = [
    { id: 'all', name: 'All Questions', count: faqItems.length },
    { id: 'course', name: 'Course Access', count: faqItems.filter(f => f.category === 'course').length },
    { id: 'licensing', name: 'Licensing Process', count: faqItems.filter(f => f.category === 'licensing').length },
    { id: 'technical', name: 'Technical Issues', count: faqItems.filter(f => f.category === 'technical').length },
    { id: 'billing', name: 'Billing & Payments', count: faqItems.filter(f => f.category === 'billing').length }
  ];

  const filteredFAQs = selectedCategory === 'all' 
    ? faqItems 
    : faqItems.filter(f => f.category === selectedCategory);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setContactForm(prev => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate message length
    if (contactForm.message.length > 500) {
      alert('Message must be 500 characters or less.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Create FormData with application/x-www-form-urlencoded format
      const formData = new FormData();
      formData.append('name', contactForm.name);
      formData.append('email', contactForm.email);
      formData.append('subject', contactForm.subject);
      formData.append('message', contactForm.message);
      formData.append('priority', contactForm.priority);

      const response = await fetch('https://readdy.ai/api/form/d3g1tnhrrun81ijtp0tg', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        setSubmitStatus('success');
        setContactForm({
          name: '',
          email: '',
          subject: '',
          message: '',
          priority: 'normal'
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="container mx-auto px-6 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Support Center
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get help with your course, licensing questions, or technical issues. We're here to ensure your success.
            </p>
          </div>

          {/* Quick Contact Options */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Email Support</h3>
              <p className="text-gray-600 text-sm mb-4">Get detailed help via email</p>
              <a
                href="mailto:Info@georgiadealerlicense.com"
                className="bg-yellow-500 text-black px-4 py-2 rounded-lg font-medium hover:bg-yellow-400 transition-colors text-sm"
              >
                Send Email
              </a>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-question-answer-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Live Consultation</h3>
              <p className="text-gray-600 text-sm mb-4">Book a one-on-one call</p>
              <button
                onClick={() => navigate('/consulting')}
                className="bg-yellow-500 text-black px-4 py-2 rounded-lg font-medium hover:bg-yellow-400 transition-colors text-sm cursor-pointer whitespace-nowrap"
              >
                Book Call
              </button>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Response Time</h3>
              <p className="text-gray-600 text-sm mb-4">We typically respond within</p>
              <div className="text-yellow-600 font-semibold">24 Hours</div>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* FAQ Section */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Frequently Asked Questions
                </h2>

                {/* Category Filter */}
                <div className="mb-6">
                  <div className="flex flex-wrap gap-2">
                    {categories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => setSelectedCategory(category.id)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                          selectedCategory === category.id
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {category.name} ({category.count})
                      </button>
                    ))}
                  </div>
                </div>

                {/* FAQ Items */}
                <div className="space-y-4">
                  {filteredFAQs.map((faq) => (
                    <div key={faq.id} className="border border-gray-200 rounded-lg">
                      <button
                        onClick={() => setOpenFAQ(openFAQ === faq.id ? '' : faq.id)}
                        className="w-full text-left p-4 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                      >
                        <span className="font-medium text-gray-900">{faq.question}</span>
                        <i className={`ri-${openFAQ === faq.id ? 'subtract' : 'add'}-line text-gray-500`}></i>
                      </button>
                      {openFAQ === faq.id && (
                        <div className="px-4 pb-4">
                          <p className="text-gray-600">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Still Need Help?
                </h3>
                <p className="text-gray-600 text-sm mb-6">
                  Can't find what you're looking for? Send us a message and we'll get back to you quickly.
                </p>

                <form id="support-contact-form" onSubmit={handleContactSubmit} data-readdy-form className="space-y-4">
                  {submitStatus === 'success' && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-4">
                      <div className="flex items-center">
                        <i className="ri-check-circle-line text-lg mr-2"></i>
                        <span className="font-medium">Message sent successfully! We'll respond within 24 hours.</span>
                      </div>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-4">
                      <div className="flex items-center">
                        <i className="ri-error-warning-line text-lg mr-2"></i>
                        <span className="font-medium">Failed to send message. Please try again or email us directly.</span>
                      </div>
                    </div>
                  )}

                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={contactForm.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={contactForm.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    />
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subject
                    </label>
                    <input
                      type="text"
                      name="subject"
                      required
                      value={contactForm.subject}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    />
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                    <select
                      name="priority"
                      value={contactForm.priority}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent pr-8"
                    >
                      <option value="low">Low - General Question</option>
                      <option value="normal">Normal - Course Help</option>
                      <option value="high">High - Urgent Issue</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Message
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      value={contactForm.message}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none"
                      placeholder="Please describe your question or issue in detail..."
                    />
                    <div className="text-right text-xs text-gray-500 mt-1">
                      {contactForm.message.length}/500 characters
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting || contactForm.message.length > 500}
                    className="w-full bg-yellow-500 text-black py-2 rounded-lg font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line mr-2"></i>
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              </div>

              {/* Support Hours */}
              <div className="bg-gray-100 rounded-2xl p-6 mt-6">
                <h4 className="font-semibold text-gray-900 mb-3">Support Hours</h4>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span>Monday - Friday</span>
                    <span>9 AM - 6 PM EST</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday</span>
                    <span>10 AM - 4 PM EST</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday</span>
                    <span>Closed</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Emergency issues will be addressed within 24 hours regardless of business hours.
                </p>
              </div>
            </div>
          </div>

          {/* Additional Resources */}
          <div className="mt-12 bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Additional Resources
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                    <i className="ri-book-open-line text-2xl text-blue-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Learning Resources</h3>
                    <p className="text-gray-600">Additional materials and guides</p>
                  </div>
                </div>
                <p className="text-gray-700 mb-4">
                  Access our comprehensive library of downloadable resources, templates, and reference materials to support your licensing journey.
                </p>
                <button 
                  onClick={() => navigate('/resources')}
                  className="text-blue-600 hover:text-blue-700 font-medium text-sm cursor-pointer whitespace-nowrap"
                >
                  View Resources →
                </button>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                    <i className="ri-video-line text-2xl text-green-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Video Tutorials</h3>
                    <p className="text-gray-600">Step-by-step video lessons</p>
                  </div>
                </div>
                <p className="text-gray-700 mb-4">
                  Watch our complete video course with detailed explanations of every step in the Georgia dealer licensing process.
                </p>
                <button 
                  onClick={() => navigate('/course')}
                  className="text-blue-600 hover:text-blue-700 font-medium text-sm cursor-pointer whitespace-nowrap"
                >
                  Watch Videos →
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}